import { useSurveyAuditsBySubject, useStartAudit, useDeleteSurveyAudit } from './useSurveyAudit';
import { SurveyAuditTableData } from '../types';
import { AuditUserRole } from '../dtos';

/**
 * Creates data management hooks for survey audits
 * Transforms enriched DTOs to table data format
 *
 * @param subjectId - Subject ID for scoping audits
 * @param tenantId - Tenant ID (for context, may not be used directly)
 * @param domainId - Domain ID (for context, may not be used directly)
 */
export const createAuditsDataManagementHooks = (
  subjectId: string,
  _tenantId: string,
  _domainId: string
) => {
  /**
   * Query hook: Get audits from backend (COMPLETED and PUBLISHED only)
   * Backend enriches with surveyTitle, responseCount, and questionCount
   * Types match automatically via SurveyAuditTableData alias
   * Note: IN_PROGRESS audits appear in "Ready to Audit" tab instead
   */
  const useAuditsQuery = () => {
    const query = useSurveyAuditsBySubject(subjectId, {
      status: ['COMPLETED', 'PUBLISHED'],
      enabled: !!subjectId,
    });

    return {
      ...query,
      // Direct passthrough - types already match due to alias
      data: query.data?.data?.audits || [],
      refetch: query.refetch,
      isFetching: query.isFetching,
    };
  };

  /**
   * Create mutation wrapper: Start new audit
   */
  const useStartAuditWrapper = () => {
    const startMutation = useStartAudit();

    return {
      mutateAsync: async (params: {
        surveyInstanceId: string;
        userRole: AuditUserRole;
        surveyId: string;
      }): Promise<SurveyAuditTableData> => {
        const response = await startMutation.mutateAsync(params);

        // Guard against undefined response.data
        if (!response.data) {
          throw new Error('Failed to create audit: no data returned');
        }

        // Transform single audit DTO to table data
        // Note: This won't have enriched fields yet, they will be populated on refetch
        return {
          id: response.data.id,
          surveyInstanceId: response.data.surveyInstanceId,
          surveyId: response.data.surveyId,
          tenantId: response.data.tenantId,
          domainId: response.data.domainId,
          subjectId: response.data.subjectId,
          status: response.data.status,
          auditedBy: response.data.auditedBy,
          auditedByRole: response.data.auditedByRole,
          surveyTitle: '', // Will be populated on refetch
          responseCount: 0, // Will be populated on refetch
          questionCount: 0, // Will be populated on refetch
          startedAt: response.data.startedAt,
          completedAt: response.data.completedAt,
          publishedAt: response.data.publishedAt,
          isLocked: response.data.isLocked,
          createdAt: response.data.createdAt,
          updatedAt: response.data.updatedAt,
        };
      },
      isPending: startMutation.isPending,
    };
  };

  /**
   * Delete mutation wrapper
   */
  const useDeleteAuditWrapper = () => {
    const deleteMutation = useDeleteSurveyAudit();

    return {
      mutateAsync: async (auditId: string): Promise<void> => {
        await deleteMutation.mutateAsync(auditId);
      },
      isPending: deleteMutation.isPending,
    };
  };

  return {
    useAuditsQuery,
    useStartAuditWrapper,
    useDeleteAuditWrapper,
  };
};
